import type { JSX, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  formatRate,
  hasOutcome,
  qualityFix,
  qualityHeadline,
  type Outcome,
  type SearchQualityNotice as QualityNotice,
} from '../suggestions/model';
import { Note } from '../workspace/ui';

/**
 * What outreach got out of a folder (or a schedule): how many of its creators
 * posted, how many joined, and posts per 100 leads — the one number that
 * compares a small folder with a big one. Renders nothing until there is
 * something to say; a folder nobody has messaged yet isn't "0 posted".
 */
export function OutcomeChips({ outcome, className }: { outcome: Outcome | null | undefined; className?: string }): JSX.Element | null {
  if (!hasOutcome(outcome)) return null;
  const detail = [
    outcome.leads != null ? `${outcome.leads.toLocaleString()} leads` : null,
    outcome.messaged != null ? `${outcome.messaged.toLocaleString()} messaged` : null,
    outcome.replied != null ? `${outcome.replied.toLocaleString()} replied` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <ul aria-label="Outreach results" title={detail || undefined} className={cn('flex flex-wrap items-center gap-1.5', className)} data-outcomes>
      {outcome.posted != null && <Chip strong={outcome.posted > 0}>{outcome.posted.toLocaleString()} posted</Chip>}
      {outcome.joined != null && <Chip>{outcome.joined.toLocaleString()} joined</Chip>}
      {outcome.postsPer100 != null && <Chip>{formatRate(outcome.postsPer100)} posts / 100 leads</Chip>}
    </ul>
  );
}

function Chip({ children, strong }: { children: ReactNode; strong?: boolean }): JSX.Element {
  return (
    <li
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[11px] font-medium leading-none tabular-nums',
        strong ? 'border-success/30 bg-success/10 text-success-strong' : 'border-border bg-muted/40 text-foreground/80',
      )}
    >
      {children}
    </li>
  );
}

/** A folder card's one-line warning; the folder itself has the detail. */
export function SearchQualityFlag({ notice }: { notice: QualityNotice | null | undefined }): JSX.Element | null {
  if (!notice) return null;
  return (
    <span
      data-quality-flag
      title={qualityHeadline(notice)}
      className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-warning/35 bg-warning/10 px-1.5 py-0.5 text-[11px] font-medium leading-none text-pending-strong"
    >
      <AlertTriangle className="h-3 w-3" aria-hidden />
      {notice.irrelevantPct != null ? `${notice.irrelevantPct}% flagged irrelevant` : 'Search quality'}
    </span>
  );
}

/**
 * The `search_quality` notice in a folder's header: outreachers keep flagging
 * its leads as irrelevant. Shows what they said and the fix Discovery
 * suggests — a suggestion; nothing about the search changes until someone
 * edits it.
 */
export function SearchQualityNotice({ notice, findHref }: { notice: QualityNotice | null | undefined; findHref: string }): JSX.Element | null {
  if (!notice) return null;
  const fix = qualityFix(notice);
  return (
    <Note
      tone="warning"
      role="status"
      action={
        <Button asChild size="sm" variant="outline">
          <Link to={findHref}>Adjust the search</Link>
        </Button>
      }
    >
      <div data-quality-notice className="space-y-1.5">
        <p className="font-medium">{qualityHeadline(notice)}</p>
        {notice.notes.length > 0 && (
          <div>
            <p className="text-muted-foreground">What outreachers said most:</p>
            <ul className="mt-0.5 list-disc space-y-0.5 pl-5">
              {notice.notes.map((n) => (
                <li key={n.note}>
                  “{n.note}”{n.count != null && <span className="text-muted-foreground"> · {n.count.toLocaleString()}×</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
        {fix && (
          <p>
            <span className="font-medium">Suggested fix:</span> {fix}
          </p>
        )}
      </div>
    </Note>
  );
}
